// useAiVoice — the shared speech engine behind <AiModule/>. Extracted from
// the old SyqiOverviewVideo so every screen's AI module (SyQi home, every
// tool's deity panel) drives audio and lip-sync the same way.
//
// Two sources of sound:
//  • the day TRACK — one pre-rendered, seekable audio file for a whole script
//    (today's overview). Play/pause/seek with a scrubber.
//  • live TTS — arbitrary text (a chat reply, a section read-out) split into
//    sentences. Sentence 0's request fires immediately, each next sentence is
//    fetched while the prior one plays, so speech starts as fast as possible.
//
// The avatar stage never plays audio itself; this hook owns playback and
// drives the stage's lip-sync clock via play()/tick()/stop(). When no avatar
// is mounted (video toggled off) every avatar call is a no-op and the hook
// degrades cleanly to a plain audio read-out.
import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { Platform } from 'react-native';
import { Audio, type AVPlaybackStatus } from 'expo-av';
import { apiFetch } from '../lib/api';
import type { LipsyncFrame, VideoAvatarHandle } from './videoAvatarTypes';

const MIN_SENTENCE_LEN = 25;
const TICK_MS = 250;

export interface VoiceTrack {
  audio: string;
  mime: string;
  lipsync: LipsyncFrame[];
}

/** Splits into sentences, folding fragments shorter than MIN_SENTENCE_LEN
 * forward into the next sentence so tiny trailing "!" / "Ok." bits don't
 * become their own (awkwardly clipped) TTS request. */
function splitSentences(text: string): string[] {
  const raw = text
    .trim()
    .split(/(?<=[.!?])\s+/)
    .map((s) => s.trim())
    .filter(Boolean);
  const out: string[] = [];
  let pending = '';
  for (const s of raw) {
    pending = pending ? `${pending} ${s}` : s;
    if (pending.length >= MIN_SENTENCE_LEN) {
      out.push(pending);
      pending = '';
    }
  }
  if (pending) out.push(pending);
  return out;
}

interface TtsResponse {
  audio: string;
  mime?: string;
  lipsync?: LipsyncFrame[];
}

interface Clip {
  id: string;
  audio: string;
  mime: string;
  lipsync: LipsyncFrame[];
}

async function fetchClip(text: string, id: string): Promise<Clip> {
  const res = await apiFetch<TtsResponse>('/api/tts?withLipsync=1', {
    method: 'POST',
    body: JSON.stringify({ text, voice: 'syqi' }),
  });
  return { id, audio: res.audio, mime: res.mime ?? 'audio/mpeg', lipsync: res.lipsync ?? [] };
}

export interface UseAiVoiceOptions {
  /** The mounted avatar stage, when video is on. Omit for audio-only mode. */
  avatarRef?: RefObject<VideoAvatarHandle | null>;
  /** Pre-rendered seekable track for the primary script, when one exists. */
  track?: VoiceTrack | null;
}

export function useAiVoice({ avatarRef, track }: UseAiVoiceOptions) {
  const [speaking, setSpeaking] = useState(false);
  const [ttsLoading, setTtsLoading] = useState(false);
  const [ttsError, setTtsError] = useState(false);
  const [trackPlaying, setTrackPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const [duration, setDuration] = useState(0);

  const sessionRef = useRef(0); // bumped to invalidate an in-flight playback chain
  const nativeSoundRef = useRef<Audio.Sound | null>(null);
  const webAudioRef = useRef<HTMLAudioElement | null>(null);
  const trackWebRef = useRef<HTMLAudioElement | null>(null);
  const trackSoundRef = useRef<Audio.Sound | null>(null);
  const trackRef = useRef<VoiceTrack | null | undefined>(track);
  trackRef.current = track;

  const avatar = useCallback(() => avatarRef?.current ?? null, [avatarRef]);

  // ── Day track ────────────────────────────────────────────────────────────
  const ensureTrackWeb = useCallback(() => {
    if (trackWebRef.current) return trackWebRef.current;
    const t = trackRef.current;
    if (!t) return null;
    const audio = new window.Audio(`data:${t.mime};base64,${t.audio}`);
    const lipsyncEnd = t.lipsync.length ? t.lipsync[t.lipsync.length - 1].time : 0;
    // Chrome reports Infinity for some data-URI mp3s — and for a multi-MB one
    // it may never fire loadedmetadata at all until playback is requested
    // (readyState sits at 0). Either way the lip-sync track's last frame time
    // IS the duration, so seed from that and only refine if real metadata lands.
    const applyDuration = () => {
      const d = audio.duration;
      setDuration(Number.isFinite(d) && d > 0 ? d : lipsyncEnd);
    };
    audio.addEventListener('loadedmetadata', applyDuration);
    audio.addEventListener('durationchange', applyDuration);
    audio.addEventListener('timeupdate', () => {
      setPosition(audio.currentTime);
      avatar()?.tick(audio.currentTime);
    });
    audio.addEventListener('ended', () => {
      setTrackPlaying(false);
      avatar()?.stop();
    });
    audio.preload = 'metadata';
    audio.load(); // Chrome won't start fetching a big data: URI on its own.
    trackWebRef.current = audio;
    return audio;
  }, [avatar]);

  const ensureTrackSound = useCallback(async () => {
    if (trackSoundRef.current) return trackSoundRef.current;
    const t = trackRef.current;
    if (!t) return null;
    const sound = new Audio.Sound();
    await sound.loadAsync({ uri: `data:${t.mime};base64,${t.audio}` });
    await sound.setProgressUpdateIntervalAsync(TICK_MS);
    sound.setOnPlaybackStatusUpdate((st: AVPlaybackStatus) => {
      if (!st.isLoaded) return;
      setPosition(st.positionMillis / 1000);
      if (st.durationMillis) setDuration(st.durationMillis / 1000);
      avatar()?.tick(st.positionMillis / 1000);
      if (st.didJustFinish) {
        setTrackPlaying(false);
        avatar()?.stop();
      }
    });
    trackSoundRef.current = sound;
    return sound;
  }, [avatar]);

  // Preload whenever a (new) track lands so the scrubber has a duration
  // before the first press, and drop any previously loaded one. Keyed on the
  // audio CONTENT, not the object: a parent re-fetch hands us a fresh object
  // with identical bytes, and rebuilding the element on that would reset
  // playback position and duration for no reason.
  useEffect(() => {
    trackWebRef.current?.pause();
    trackWebRef.current = null;
    trackSoundRef.current?.unloadAsync().catch(() => {});
    trackSoundRef.current = null;
    setTrackPlaying(false);
    setPosition(0);
    if (!track) {
      setDuration(0);
      return;
    }
    // The lip-sync track's last frame time is the render's exact duration, so
    // the scrubber is usable immediately — no waiting on browser metadata.
    setDuration(track.lipsync.length ? track.lipsync[track.lipsync.length - 1].time : 0);
    if (Platform.OS === 'web') {
      ensureTrackWeb();
    } else {
      ensureTrackSound()
        .then(async (sound) => {
          const st = await sound?.getStatusAsync();
          if (st?.isLoaded && st.durationMillis) setDuration(st.durationMillis / 1000);
        })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [track?.audio]);

  const pauseTrack = useCallback(async () => {
    trackWebRef.current?.pause();
    await trackSoundRef.current?.pauseAsync().catch(() => {});
    setTrackPlaying(false);
  }, []);

  // ── Live TTS ─────────────────────────────────────────────────────────────
  const stopCurrentClip = useCallback(async () => {
    if (Platform.OS === 'web') {
      const audio = webAudioRef.current;
      webAudioRef.current = null;
      if (audio) {
        audio.pause();
        audio.src = '';
      }
    } else {
      const sound = nativeSoundRef.current;
      nativeSoundRef.current = null;
      if (sound) {
        await sound.stopAsync().catch(() => {});
        await sound.unloadAsync().catch(() => {});
      }
    }
    avatar()?.stop();
  }, [avatar]);

  const playClipWeb = useCallback(
    (clip: Clip, session: number) => {
      return new Promise<void>((resolve, reject) => {
        const audio = new window.Audio(`data:${clip.mime};base64,${clip.audio}`);
        webAudioRef.current = audio;
        avatar()?.play(clip.id, clip.lipsync);
        const tickId = window.setInterval(() => {
          if (sessionRef.current !== session) return;
          avatar()?.tick(audio.currentTime);
        }, TICK_MS);
        const cleanup = () => window.clearInterval(tickId);
        audio.addEventListener('ended', () => {
          cleanup();
          resolve();
        });
        audio.addEventListener('error', () => {
          cleanup();
          reject(new Error('audio playback failed'));
        });
        audio.play().catch((e) => {
          cleanup();
          reject(e instanceof Error ? e : new Error('audio play() rejected'));
        });
      });
    },
    [avatar]
  );

  const playClipNative = useCallback(
    (clip: Clip, session: number) => {
      return new Promise<void>((resolve, reject) => {
        (async () => {
          try {
            const sound = new Audio.Sound();
            nativeSoundRef.current = sound;
            await sound.loadAsync({ uri: `data:${clip.mime};base64,${clip.audio}` });
            if (sessionRef.current !== session) {
              await sound.unloadAsync().catch(() => {});
              resolve();
              return;
            }
            await sound.setProgressUpdateIntervalAsync(TICK_MS);
            avatar()?.play(clip.id, clip.lipsync);
            sound.setOnPlaybackStatusUpdate((status: AVPlaybackStatus) => {
              if (!status.isLoaded || sessionRef.current !== session) return;
              avatar()?.tick(status.positionMillis / 1000);
              if (status.didJustFinish) {
                sound.unloadAsync().catch(() => {});
                resolve();
              }
            });
            await sound.playAsync();
          } catch (e) {
            reject(e instanceof Error ? e : new Error('native audio playback failed'));
          }
        })();
      });
    },
    [avatar]
  );

  const speak = useCallback(
    async (text: string) => {
      const clean = text.trim();
      if (!clean) return;
      await pauseTrack();
      await stopCurrentClip();
      const session = ++sessionRef.current;
      const sentences = splitSentences(clean);
      if (!sentences.length) return;

      setTtsError(false);
      setTtsLoading(true);

      const requestId = `${session}`;
      // Look-ahead of 1, NOT all-concurrent: the TTS server is CPU-bound, so
      // firing every sentence at once slows down the first clip — the thing
      // that defines perceived latency. Fetch clip 0 alone; start clip i+1
      // only once clip i is in hand and playing.
      const clipPromises: Array<Promise<Clip> | null> = sentences.map(() => null);
      const ensureFetch = (i: number): Promise<Clip> | null => {
        if (i >= sentences.length) return null;
        if (!clipPromises[i]) clipPromises[i] = fetchClip(sentences[i], `${requestId}-${i}`);
        return clipPromises[i];
      };
      ensureFetch(0);

      try {
        for (let i = 0; i < sentences.length; i += 1) {
          setTtsLoading(true); // visible "voice generating" state whenever we wait on a clip
          const clip = await ensureFetch(i)!;
          if (sessionRef.current !== session) return; // superseded by a newer speak()
          ensureFetch(i + 1); // prefetch the next clip while this one plays
          setTtsLoading(false);
          setSpeaking(true);
          if (Platform.OS === 'web') {
            await playClipWeb(clip, session);
          } else {
            await playClipNative(clip, session);
          }
          if (sessionRef.current !== session) return;
        }
      } catch {
        if (sessionRef.current === session) setTtsError(true);
      } finally {
        if (sessionRef.current === session) {
          setTtsLoading(false);
          setSpeaking(false);
        }
      }
    },
    [pauseTrack, stopCurrentClip, playClipWeb, playClipNative]
  );

  const stopSpeaking = useCallback(async () => {
    sessionRef.current += 1;
    await stopCurrentClip();
    setSpeaking(false);
    setTtsLoading(false);
  }, [stopCurrentClip]);

  /** The play/pause button. Plays the pre-rendered track when there is one,
   *  otherwise speaks `fallbackText` through the live TTS pipeline. */
  const togglePlay = useCallback(
    async (fallbackText?: string) => {
      if (speaking || ttsLoading) {
        await stopSpeaking();
        return;
      }
      if (!track) {
        if (fallbackText) await speak(fallbackText);
        return;
      }
      if (trackPlaying) {
        await pauseTrack();
        avatar()?.stop();
        return;
      }
      sessionRef.current += 1; // cancel any in-flight chat-reply chain
      await stopCurrentClip();
      if (Platform.OS === 'web') {
        const audio = ensureTrackWeb();
        if (!audio) return;
        avatar()?.play('track', track.lipsync, audio.currentTime);
        await audio.play();
      } else {
        const sound = await ensureTrackSound();
        if (!sound) return;
        const st = await sound.getStatusAsync();
        const pos = st.isLoaded ? st.positionMillis / 1000 : 0;
        avatar()?.play('track', track.lipsync, pos);
        await sound.playAsync();
      }
      setTrackPlaying(true);
    },
    [speaking, ttsLoading, stopSpeaking, track, trackPlaying, speak, pauseTrack, avatar, stopCurrentClip, ensureTrackWeb, ensureTrackSound]
  );

  const seek = useCallback(
    async (frac: number) => {
      if (!track || !Number.isFinite(duration) || duration <= 0) return;
      const t = Math.max(0, Math.min(1, frac)) * duration;
      if (!Number.isFinite(t)) return;
      setPosition(t);
      if (Platform.OS === 'web') {
        const audio = ensureTrackWeb();
        if (audio) audio.currentTime = t;
      } else {
        const sound = await ensureTrackSound();
        await sound?.setPositionAsync(t * 1000);
      }
      if (trackPlaying) avatar()?.play('track', track.lipsync, t);
    },
    [track, duration, trackPlaying, avatar, ensureTrackWeb, ensureTrackSound]
  );

  useEffect(() => {
    return () => {
      sessionRef.current += 1;
      stopCurrentClip();
      trackWebRef.current?.pause();
      trackSoundRef.current?.unloadAsync().catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    speak,
    stopSpeaking,
    togglePlay,
    seek,
    speaking,
    ttsLoading,
    ttsError,
    trackPlaying,
    position,
    duration,
    /** True whenever any audio is currently sounding. */
    playing: trackPlaying || speaking,
  };
}
